import React, { useState, useEffect } from 'react';
import { formatMontant, getDeviseCourante } from '../../utils/format';
import { AlertTriangle, CheckCircle, MessageSquare } from 'lucide-react';
import api from '../../services/api';
import { Button, Select } from '../../components/common/Input';
import InputMontant from '../../components/common/InputMontant';
import toast from 'react-hot-toast';
import FormRetourCommission from './FormRetourCommission';

// ──────────────────────────────────────────────────────────────
// FormRetourSoir — clôture d'UNE sortie (mode "par sortie")
//
// Règle d'écart v2 :
//   Écart = Montant remis + Valeur retournée − Valeur sortie
//   - Écart = 0  → validation directe
//   - Écart < 0  → explication OBLIGATOIRE, montant ajouté à la dette
//   - Écart > 0  → REFUSÉ (le bouton de validation est bloqué)
// ──────────────────────────────────────────────────────────────

export default function FormRetourSoir({ sortie, onSuccess, onCancel }) {
  const [sortieDetail, setSortieDetail] = useState(null);
  const [lignesRetour, setLignesRetour] = useState([]);
  const [montantRemis, setMontantRemis]   = useState('');
  const [modePaiement, setModePaiement]   = useState('especes');
  const [explication, setExplication]     = useState('');
  const [notes, setNotes]                 = useState('');
  const [loading, setLoading]             = useState(false);
  const [modeVendeur, setModeVendeur]     = useState(null); // 'standard' | 'commission'

  // Mode de rémunération du commerce (réglage Paramètres). Défaut : standard.
  useEffect(() => {
    api.get('/parametres')
      .then(r => setModeVendeur(r.data?.mode_vendeur === 'commission' ? 'commission' : 'standard'))
      .catch(() => setModeVendeur('standard'));
  }, []);

  useEffect(() => {
    api.get(`/vendeurs/sorties/${sortie.id}/bon`)
      .then(res => {
        setSortieDetail(res.data);
        // Par défaut : tout est retourné (le vendeur ajustera à la baisse au fur et à mesure)
        setLignesRetour(res.data.lignes.map(l => ({
          sortie_ligne_id: l.id,
          variante_id: l.variante_id,
          produit_nom: l.produit_nom,
          modele: l.modele,
          taille: l.taille || '',
          couleur: l.couleur,
          quantite_confiee: parseInt(l.quantite_confiee),
          prix_vente_unitaire: parseFloat(l.prix_vente_unitaire),
          quantite_retournee: parseInt(l.quantite_confiee),
        })));
      })
      .catch(() => toast.error('Erreur chargement sortie'));
  }, [sortie.id]);

  const mettreAJourRetour = (i, val) => {
    const max = lignesRetour[i].quantite_confiee;
    const v = Math.min(Math.max(0, parseInt(val) || 0), max);
    setLignesRetour(prev => prev.map((l, idx) =>
      idx === i ? { ...l, quantite_retournee: v } : l
    ));
  };

  // ── Calculs temps réel (NOUVELLE formule) ───────────────────
  const valeurSortie    = lignesRetour.reduce((s, l) =>
    s + l.quantite_confiee * l.prix_vente_unitaire, 0);
  const valeurRetournee = lignesRetour.reduce((s, l) =>
    s + l.quantite_retournee * l.prix_vente_unitaire, 0);
  const valeurVendue    = valeurSortie - valeurRetournee;
  const totalPiecesRetour = lignesRetour.reduce((s, l) => s + l.quantite_retournee, 0);
  const totalPiecesVendues = lignesRetour.reduce((s, l) =>
    s + (l.quantite_confiee - l.quantite_retournee), 0);

  const montantRemisNum = parseFloat(montantRemis) || 0;
  const aSaisiMontant   = montantRemis !== '' && !isNaN(montantRemisNum);
  // Bonne formule : Écart = remis + retourné - sortie
  const ecart           = montantRemisNum + valeurRetournee - valeurSortie;

  // ── Règles de validation pour le bouton ─────────────────────
  const ecartPositif         = ecart > 0;
  const ecartNegatifNonExpl  = ecart < 0 && !explication.trim();
  const submitBloque         = !aSaisiMontant || ecartPositif || ecartNegatifNonExpl;

  const handleSubmit = async () => {
    if (submitBloque) return;
    setLoading(true);
    try {
      const res = await api.post(`/vendeurs/sorties/${sortie.id}/retour`, {
        lignes_retour: lignesRetour,
        montant_remis: montantRemisNum,
        mode_paiement_retour: modePaiement,
        explication_ecart: explication || null,
        notes: notes || null,
      });
      if (res.data.dette_ajoutee > 0) {
        toast(`Écart de ${formatMontant(Math.abs(ecart))} ajouté à la dette de ${sortie.prenom}`, {
          duration: 5000, icon: '⚠️',
        });
      }
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erreur serveur');
    } finally {
      setLoading(false);
    }
  };

  if (!sortieDetail || modeVendeur === null) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin w-8 h-8 border-2 border-yellow-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  // Aiguillage : si le commerce est en mode commission ET ce vendeur est payé
  // à la commission, on bascule sur le formulaire dédié. Sinon, écran standard.
  if (modeVendeur === 'commission' && sortieDetail.sortie?.type_remuneration === 'commission') {
    return (
      <FormRetourCommission
        sortie={sortie}
        sortieDetail={sortieDetail}
        onSuccess={onSuccess}
        onCancel={onCancel}
      />
    );
  }

  return (
    <div className="space-y-5">
      {/* En-tête */}
      <div className="bg-gray-800 rounded-xl p-4">
        <p className="text-white font-semibold">{sortie.prenom} {sortie.nom} — Retour du soir</p>
        <p className="text-gray-400 text-sm">
          {sortieDetail.sortie?.date} · {sortieDetail.lignes?.length} article(s) confié(s)
        </p>
      </div>

      {/* Articles : quantité retournée */}
      <div>
        <h3 className="text-sm font-semibold text-yellow-400 uppercase tracking-wider mb-3">
          Articles retournés
        </h3>
        <div className="space-y-2">
          {lignesRetour.map((l, i) => {
            const qteVendue = l.quantite_confiee - l.quantite_retournee;
            const mtVendu = qteVendue * l.prix_vente_unitaire;
            return (
              <div key={i} className="bg-gray-800 rounded-xl p-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-medium truncate">{l.produit_nom}</p>
                    <p className="text-gray-500 text-xs">
                      {l.modele} {l.taille ? `· ${l.taille}` : ''} · {l.couleur}
                    </p>
                    <p className="text-gray-500 text-xs">
                      Confié: <span className="text-yellow-400">{l.quantite_confiee}</span> ·
                      Prix: {formatMontant(l.prix_vente_unitaire)}
                    </p>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <div className="text-center">
                      <p className="text-xs text-gray-500 mb-1">Retourné</p>
                      <input
                        type="number" min="0" max={l.quantite_confiee}
                        value={l.quantite_retournee === 0 ? '' : l.quantite_retournee}
                        onChange={e => mettreAJourRetour(i, e.target.value)}
                        placeholder="0"
                        className="w-16 bg-gray-700 border border-gray-600 text-white rounded-lg px-2 py-1.5 text-sm text-center focus:outline-none focus:border-yellow-500"
                      />
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-gray-500 mb-1">Vendu</p>
                      <p className={`font-bold text-sm ${qteVendue > 0 ? 'text-green-400' : 'text-gray-600'}`}>
                        {qteVendue}
                      </p>
                    </div>
                    <div className="text-right min-w-16">
                      <p className="text-xs text-gray-500 mb-1">Montant</p>
                      <p className="text-yellow-400 text-sm font-bold">{formatMontant(mtVendu)}</p>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Résumé calculs (la formule est expliquée visuellement) */}
      <div className="bg-gray-800 rounded-xl p-4 space-y-2">
        <h3 className="text-sm font-semibold text-gray-300 mb-3">Récapitulatif</h3>
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-400">Valeur sortie (articles confiés)</span>
          <span className="text-yellow-400 font-medium">{formatMontant(valeurSortie)}</span>
        </div>
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-400">Valeur retournée ({totalPiecesRetour} pcs)</span>
          <span className="text-blue-400 font-medium">{formatMontant(valeurRetournee)}</span>
        </div>
        <div className="flex items-center justify-between text-sm border-t border-gray-700 pt-2 mt-1">
          <span className="text-gray-300 font-medium">
            Valeur vendue ({totalPiecesVendues} pcs) — à encaisser
          </span>
          <span className="text-green-400 font-bold">{formatMontant(valeurVendue)}</span>
        </div>
      </div>

      {/* Montant remis */}
      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-yellow-400 uppercase tracking-wider">
          Argent remis par le vendeur
        </h3>
        <div>
          <label className="block text-sm text-gray-400 mb-1.5">Montant remis ({getDeviseCourante()}) *</label>
          <div className="relative">
            <InputMontant
              value={montantRemis}
              onChange={setMontantRemis}
              devise={null}
              placeholder={`À encaisser : ${valeurVendue.toLocaleString()} ${getDeviseCourante()}`}
              className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg px-4 py-3 pr-14 text-lg font-bold focus:outline-none focus:border-yellow-500"
            />
            <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 text-sm font-semibold pointer-events-none">{getDeviseCourante()}</span>
          </div>
        </div>
        <Select label="Mode de paiement" value={modePaiement} onChange={e => setModePaiement(e.target.value)}>
          <option value="especes">💵 Espèces</option>
          <option value="orange_money">🟠 Orange Money</option>
          <option value="mtn_money">🟡 MTN Money</option>
          <option value="mixte">🔀 Mixte</option>
        </Select>

        {/* Aperçu écart */}
        {aSaisiMontant && (
          <div className={`rounded-xl p-4 ${
            ecart === 0
              ? 'bg-green-900/20 border border-green-700/30'
              : ecart < 0
                ? 'bg-orange-900/30 border border-orange-700/50'
                : 'bg-red-900/40 border border-red-700/60'
          }`}>
            <div className="flex items-start gap-2">
              {ecart === 0
                ? <CheckCircle size={18} className="text-green-400 flex-shrink-0 mt-0.5" />
                : <AlertTriangle size={18} className={`flex-shrink-0 mt-0.5 ${ecart < 0 ? 'text-orange-400' : 'text-red-400'}`} />
              }
              <div className="flex-1">
                <p className={`font-bold ${
                  ecart === 0 ? 'text-green-400' : ecart < 0 ? 'text-orange-300' : 'text-red-300'
                }`}>
                  Écart : {ecart >= 0 ? '+' : ''}{formatMontant(ecart)}
                </p>
                {ecart === 0 && (
                  <p className="text-green-300 text-xs mt-0.5">
                    ✓ Équilibre parfait — clôture validable directement
                  </p>
                )}
                {ecart < 0 && (
                  <p className="text-orange-200 text-xs mt-0.5">
                    Le montant remis est inférieur à la valeur vendue.
                    Ce manque sera ajouté à la dette de {sortie.prenom} {sortie.nom}.
                    <strong className="block mt-1">Explication obligatoire ci-dessous.</strong>
                  </p>
                )}
                {ecart > 0 && (
                  <p className="text-red-200 text-xs mt-0.5">
                    Le montant remis dépasse la valeur vendue. Vérifiez les quantités retournées
                    et le montant saisi. Un remboursement de dette antérieure doit être enregistré
                    séparément (bouton « Rembourser »).
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Champ explication, conditionnel */}
        {aSaisiMontant && ecart < 0 && (
          <div>
            <label className="text-sm text-gray-400 mb-1.5 flex items-center gap-1.5">
              <MessageSquare size={14} className="text-orange-400" />
              Explication de l'écart <span className="text-red-400">*</span>
            </label>
            <textarea
              value={explication}
              onChange={e => setExplication(e.target.value)}
              placeholder="Ex : oubli, perte d'un sac, vol, vente à crédit non collectée…"
              rows={2}
              className={`w-full bg-gray-800 border text-white rounded-lg px-3 py-2 text-sm focus:outline-none ${
                !explication.trim()
                  ? 'border-red-700/60 focus:border-red-500'
                  : 'border-gray-700 focus:border-yellow-500'
              }`}
            />
            {!explication.trim() && (
              <p className="text-red-400 text-xs mt-1">
                Une explication est obligatoire pour valider une clôture avec écart.
              </p>
            )}
          </div>
        )}

        <div>
          <label className="block text-sm text-gray-400 mb-1.5">Notes (libre)</label>
          <input
            value={notes} onChange={e => setNotes(e.target.value)}
            placeholder="Notes optionnelles…"
            className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-yellow-500"
          />
        </div>
      </div>

      <div className="flex space-x-3 pt-2">
        <Button type="button" variant="secondary" onClick={onCancel} className="flex-1">
          Annuler
        </Button>
        <Button
          loading={loading}
          onClick={handleSubmit}
          disabled={submitBloque}
          className="flex-1"
          variant={ecart < 0 ? 'danger' : 'primary'}
        >
          {ecart < 0
            ? '⚠️ Enregistrer avec écart'
            : ecart > 0
              ? '⛔ Montant invalide'
              : 'Enregistrer le retour'}
        </Button>
      </div>
    </div>
  );
}
